// =====================================================================
// src/lib/graficos.js — os Gráficos do ano, sem DOM
//   (MdM `renderGraficos` 17961 · `getMonthlyData` 17842 · `makeChart`
//    17914 · `exportPDF` 18081)
//
// Cinco séries de 12 meses, do ANO escolhido, filtradas por proprietário:
//   receitaMes     — Receitas de Diárias: o valor INTEIRO da reserva no mês
//                    do check-in (18013; não é pro-rata — o Financeiro rateia)
//   despesasMes    — lançamentos de despesa por mês (`getMonthlyData`)
//   ocupacaoMes    — noites ocupadas ÷ (flats × dias do mês), em % inteiro
//   diariaMediaMes — receita pro-rata do mês ÷ noites ocupadas no mês (18057)
//   (frigobar receita × custo fica para o item 6 — a série nem existe aqui)
//
// Divergência de domínio: o MdM lê o ano do relógio do aparelho; aqui o
// ano é escolhido (o do hotel por padrão), porque um relatório anual se
// consulta em janeiro. As cores são as dele (17963–17973).
// =====================================================================
import { noites } from './reservas.js';

export const MESES_CURTOS = Object.freeze(['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']);
export const CORES = Object.freeze({ receita: '#1B3A7A', despesas: '#dc2626', ocupacao: '#059669', diariaMedia: '#7c3aed' });
export const GRAFICOS = Object.freeze([
  { chave: 'receita', titulo: 'Receitas de Diárias (R$)', serie: 'receitaMes', cor: CORES.receita, rotulo: 'Receita Diárias' },
  { chave: 'despesas', titulo: 'Despesas (R$)', serie: 'despesasMes', cor: CORES.despesas, rotulo: 'Despesas' },
  { chave: 'ocupacao', titulo: 'Taxa de Ocupação (%)', serie: 'ocupacaoMes', cor: CORES.ocupacao, rotulo: 'Taxa de Ocupação', percentual: true },
  { chave: 'diaria-media', titulo: 'Diária Média por Mês (R$/noite)', serie: 'diariaMediaMes', cor: CORES.diariaMedia, rotulo: 'Diária Média (R$/noite)', largo: true },
]);
const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const zeros = () => Array(12).fill(0);
const diasDoMes = (ano, m) => new Date(Date.UTC(Number(ano), m + 1, 0)).getUTCDate();
const iso = (ano, m, d) => `${ano}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// `getMonthlyData` (17842): soma por mês dos lançamentos do ano, por tipo
export function porMes(lancamentos, ano, tipo) {
  const meses = zeros();
  for (const l of lancamentos || []) {
    if (!l.data || l.data.slice(0, 4) !== String(ano)) continue;
    if (tipo && l.tipo !== tipo) continue;
    meses[Number(l.data.slice(5, 7)) - 1] += num(l.valor);
  }
  return meses;
}

// as séries do ano. dados = { unidades, reservas, valorDe, lancamentos };
// proprietarioId = '' para todos
export function series(dados, ano, proprietarioId) {
  const unidades = (dados.unidades || []).filter(u => !proprietarioId || u.proprietario_id === proprietarioId);
  const ids = new Set(unidades.map(u => u.id));
  const valorDe = dados.valorDe || (() => null);
  const A = String(ano);
  const reservas = (dados.reservas || []).filter(r => r.situacao !== 'cancelada' && r.check_in && ids.has(r.unidade_id));
  const receitaMes = zeros(), noitesMes = zeros(), receitaProRataMes = zeros(), ocupacaoMes = zeros(), diariaMediaMes = zeros();
  let reservasNoAno = 0;
  for (const r of reservas) {
    if (r.check_in.slice(0, 4) !== A) continue;
    receitaMes[Number(r.check_in.slice(5, 7)) - 1] += num(valorDe(r));
    reservasNoAno++;
  }
  const totalFlats = unidades.length || 1;
  for (let m = 0; m < 12; m++) {
    const mIni = iso(A, m, 1), mFimEx = m === 11 ? `${Number(A) + 1}-01-01` : iso(A, m + 1, 1);
    let occ = 0, rec = 0;
    for (const r of reservas) {
      if (!r.check_out) continue;
      const ini = r.check_in > mIni ? r.check_in : mIni, fim = r.check_out < mFimEx ? r.check_out : mFimEx;
      if (ini >= fim) continue;
      const dentro = noites(ini, fim);
      occ += dentro;
      rec += num(valorDe(r)) * (dentro / Math.max(1, noites(r.check_in, r.check_out)));
    }
    noitesMes[m] = occ; receitaProRataMes[m] = rec;
    ocupacaoMes[m] = Math.round((occ / (totalFlats * diasDoMes(A, m))) * 100);
    diariaMediaMes[m] = occ > 0 ? rec / occ : 0;
  }
  const lancs = (dados.lancamentos || []).filter(l => !proprietarioId || (l.unidade_id && ids.has(l.unidade_id)));
  const despesasMes = porMes(lancs, A, 'despesa');
  const receitasLancadas = porMes(lancs, A, 'receita').reduce((s, v) => s + v, 0);
  const totalDespesas = despesasMes.reduce((s, v) => s + v, 0);
  return { ano: A, flats: unidades.length, reservasNoAno, receitaMes, despesasMes, ocupacaoMes, noitesMes, receitaProRataMes, diariaMediaMes,
    totais: { receitas: receitaMes.reduce((s, v) => s + v, 0), receitasLancadas, despesas: totalDespesas } };
}

// os anos que fazem sentido oferecer: do primeiro check-in ao ano do hotel
export function anosDisponiveis(reservas, hoje) {
  const anos = new Set([hoje.slice(0, 4)]);
  for (const r of reservas || []) if (r.check_in) anos.add(r.check_in.slice(0, 4));
  return [...anos].sort().reverse();
}

// `makeChart` (17914): a configuração do Chart.js, para a tela só instanciar
export function configDoGrafico(g, dadosSerie) {
  const isOcup = !!g.percentual;
  return {
    type: 'line',
    data: { labels: [...MESES_CURTOS], datasets: [{ label: g.rotulo, data: dadosSerie, backgroundColor: g.cor + '33', borderColor: g.cor, borderWidth: 2, borderRadius: 4, tension: 0.3, fill: true }] },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'top' }, tooltip: { callbacks: { label: ctx => (isOcup ? `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%` : `${ctx.dataset.label}: R$ ${ctx.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`) } } },
      scales: { x: { grid: { display: false } }, y: Object.assign({ beginAtZero: true, grace: '10%', ticks: { maxTicksLimit: 6, callback: v => (isOcup ? v + '%' : v >= 1000000 ? 'R$' + (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? 'R$' + (v / 1000).toFixed(0) + 'k' : 'R$' + v) } },
        isOcup ? { suggestedMax: 108, max: 110 } : {}) },
    },
  };
}

// a tabela "Dados Mensais" do PDF (18165), sem as colunas do frigobar
export const COLUNAS_MENSAIS = Object.freeze(['Mês', 'Rec. Diárias', 'Diária Média', 'Despesas', 'Ocupação']);
export function linhasMensais(s, moeda) {
  return MESES_CURTOS.map((m, i) => [m, moeda(s.receitaMes[i]), s.diariaMediaMes[i] > 0 ? moeda(s.diariaMediaMes[i]) : '—', moeda(s.despesasMes[i]), (s.ocupacaoMes[i] || 0) + '%']);
}
export function tituloDoRelatorio(s, nomeProprietario) {
  return nomeProprietario ? `Relatório de Gestão — Flats de ${nomeProprietario}` : `Relatório de Gestão de Flats - ${s.ano}`;
}
